import { useRef, useEffect, useState, useCallback } from 'react';
import { createControllerChannel } from './ControllerChannel';
import type { ChannelInstance } from './types';

interface UseMessageControllerConfig {
	targetOrigin: string;
	onReady?: () => void;
	onMessage?: (type: string, payload: unknown, version: number) => void;
	onError?: (error: Error) => void;
	onDisconnect?: () => void;
	onRouteChange?: (path: string) => void;
	timeout?: number;
}

export function useMessageController(
	iframeRef: React.RefObject<HTMLIFrameElement>,
	config: UseMessageControllerConfig,
) {
	const [isConnected, setIsConnected] = useState(false);
	const channelRef = useRef<ChannelInstance | null>(null);
	const configRef = useRef(config);

	// Keep config ref updated
	useEffect(() => {
		configRef.current = config;
	});

	useEffect(() => {
		const iframe = iframeRef.current;
		if (!iframe) return;

		channelRef.current = createControllerChannel({
			iframe,
			targetOrigin: configRef.current.targetOrigin,
			onReady: () => {
				setIsConnected(true);
				configRef.current.onReady?.();
			},
			onMessage: configRef.current.onMessage,
			onError: configRef.current.onError,
			onDisconnect: () => {
				setIsConnected(false);
				configRef.current.onDisconnect?.();
			},
			onRouteChange: configRef.current.onRouteChange,
			timeout: configRef.current.timeout,
		});

		return () => {
			channelRef.current?.disconnect();
			channelRef.current = null;
			setIsConnected(false);
		};
	}, [iframeRef, config.targetOrigin]);

	const send = useCallback((type: string, payload: unknown, version?: number) => {
		channelRef.current?.send(type, payload, version);
	}, []);

	const on = useCallback(
		(type: string, callback: (payload: unknown, version: number) => void) => {
			return channelRef.current?.on(type, callback) ?? (() => {});
		},
		[],
	);

	const once = useCallback(
		(type: string, callback: (payload: unknown, version: number) => void) => {
			return channelRef.current?.once(type, callback) ?? (() => {});
		},
		[],
	);

	const request = useCallback(
		<T = unknown>(type: string, payload: unknown, timeoutMs?: number) => {
			if (!channelRef.current) {
				return Promise.reject(new Error('Channel not connected'));
			}
			return channelRef.current.request<T>(type, payload, timeoutMs);
		},
		[],
	);

	const reply = useCallback((requestId: string, data: unknown) => {
		channelRef.current?.reply(requestId, data);
	}, []);

	const reconnect = useCallback(() => {
		channelRef.current?.reconnect();
	}, []);

	return { isConnected, send, on, once, request, reply, reconnect };
}
